import { useState } from 'react';
import api from '@/lib/api';

interface TrackingEvent {
  id: string;
  status: string;
  location: string;
  description: string;
  timestamp: string;
}

const eventStatusLabels: Record<string, { label: string; color: string }> = {
  PENDING: { label: 'Pendente', color: 'bg-gray-100 text-gray-700' },
  DISPATCHED: { label: 'Despachado', color: 'bg-blue-100 text-blue-700' },
  IN_TRANSIT: { label: 'Em Transito', color: 'bg-yellow-100 text-yellow-700' },
  OUT_FOR_DELIVERY: { label: 'Saiu para Entrega', color: 'bg-purple-100 text-purple-700' },
  DELIVERED: { label: 'Entregue', color: 'bg-green-100 text-green-700' },
  FAILED: { label: 'Falhou', color: 'bg-red-100 text-red-700' },
  RETURNED: { label: 'Devolvido', color: 'bg-orange-100 text-orange-700' },
};

export default function TrackingPage() {
  const [trackingCode, setTrackingCode] = useState('');
  const [events, setEvents] = useState<TrackingEvent[]>([]);
  const [found, setFound] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);

  const search = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setFound(null);
    try {
      const r = await api.get(`/tracking/${trackingCode}`);
      setEvents(r.data);
      setFound(true);
    } catch {
      setEvents([]);
      setFound(false);
    }
    setLoading(false);
  };

  return (
    <div className="max-w-2xl mx-auto">
        <form onSubmit={search} className="bg-white rounded-xl p-6 shadow-sm border border-dark-100 mb-6">
          <label className="block text-sm font-medium text-dark-700 mb-2">Codigo de Rastreio</label>
          <div className="flex gap-3">
            <input
              type="text"
              value={trackingCode}
              onChange={(e) => setTrackingCode(e.target.value)}
              placeholder="Digite o codigo de rastreio..."
              className="flex-1 px-4 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
            <button
              type="submit"
              className="px-6 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </button>
          </div>
        </form>

        {found === false && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-dark-100 text-center">
            <p className="text-dark-500">Codigo de rastreio nao encontrado</p>
          </div>
        )}

        {found === true && events.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-dark-100">
            <h3 className="text-sm font-semibold text-dark-500 uppercase mb-6">Timeline</h3>
            <div className="relative">
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-dark-200"></div>
              <div className="space-y-6">
                {events.map((event, index) => (
                  <div key={event.id} className="relative flex gap-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${
                      index === 0 ? 'bg-primary-600 text-white' : 'bg-dark-100 text-dark-500'
                    }`}>
                      <div className="w-2 h-2 rounded-full bg-current"></div>
                    </div>
                    <div className="flex-1 pb-2">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${eventStatusLabels[event.status]?.color || 'bg-gray-100 text-gray-700'}`}>
                          {eventStatusLabels[event.status]?.label || event.status}
                        </span>
                        <span className="text-xs text-dark-400">
                          {new Date(event.timestamp).toLocaleDateString('pt-BR')} {new Date(event.timestamp).toLocaleTimeString('pt-BR')}
                        </span>
                      </div>
                      <p className="text-sm text-dark-700">{event.description}</p>
                      {event.location && <p className="text-xs text-dark-500 mt-1">{event.location}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
  );
}
